import { useState, useEffect, useCallback } from 'react'
import { getNostrPool } from '../lib/nostrPool'
import { RELAYS } from '../config/constants'
import { Event } from 'nostr-tools'

interface FollowList {
    following: string[]
    isLoading: boolean
    lastUpdated: number
}

export function useFollowList(pubkey?: string | null) {
    const [followList, setFollowList] = useState<FollowList>({
        following: [],
        isLoading: true,
        lastUpdated: 0
    })
    const [refreshTrigger, setRefreshTrigger] = useState(0)

    useEffect(() => {
        const pool = getNostrPool()

        if (!pool || !pubkey) {
            setFollowList({ following: [], isLoading: false, lastUpdated: 0 })
            return
        }

        setFollowList(prev => ({ ...prev, isLoading: true }))

        const sub = pool.subscribeMany(
            RELAYS,
            {
                kinds: [3],
                authors: [pubkey],
                limit: 1
            },
            {
                onevent(event: Event) {
                    // Only process if this is a newer event
                    if (event.created_at > followList.lastUpdated) {
                        const following = event.tags
                            .filter((tag: string[]) => tag[0] === 'p')
                            .map((tag: string[]) => tag[1])

                        setFollowList({
                            following,
                            isLoading: false,
                            lastUpdated: event.created_at
                        })
                    }
                },
                oneose() {
                    setFollowList(prev => ({ ...prev, isLoading: false }))
                }
            }
        )

        return () => {
            sub.close()
        }
    }, [pubkey, refreshTrigger])

    // Force refresh by incrementing trigger
    const refresh = useCallback(() => {
        setRefreshTrigger(prev => prev + 1)
    }, [])

    // Immediate update (optimistic) - for instant UI feedback
    const updateFollowing = useCallback((newFollowing: string[]) => {
        setFollowList(prev => ({
            ...prev,
            following: newFollowing
        }))
    }, [])

    const isFollowing = useCallback((targetPubkey: string) => {
        return followList.following.includes(targetPubkey)
    }, [followList.following])

    return {
        following: followList.following,
        isLoading: followList.isLoading,
        isFollowing,
        refresh,
        updateFollowing,
        followingCount: followList.following.length
    }
}
